import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, GraduationCap } from "lucide-react";
import { useLang } from "../../i18n";
import { Reveal, Overline } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink/55">
      {label}
    </label>
    <div className="mt-1">{children}</div>
  </div>
);

const VisitorForm = ({ masterclass = false }) => {
  const { lang, t } = useLang();
  const v = t.participate.visitor;
  const c = t.participate.classForm;
  const [form, setForm] = useState({ name: "", email: "", guests: 2, day: "2026-12-12" });
  const [status, setStatus] = useState("idle");
  const [avail, setAvail] = useState(null);

  const loadAvail = () => {
    axios
      .get(`${API}/participate/masterclass-availability`)
      .then((r) => setAvail(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (masterclass) loadAvail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterclass]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/participate/visitor`, {
        ...form,
        guests: Number(form.guests),
        day: masterclass ? "2026-12-11" : form.day,
        type: masterclass ? "masterclass" : "visitor",
        lang,
      });
      setStatus("done");
      toast.success(t.participate.successToast);
    } catch (err) {
      setStatus("idle");
      if (masterclass && err.response?.status === 409) {
        toast.error(c.classFull);
        loadAvail();
      } else {
        toast.error(err.response?.status === 429 ? t.participate.tooMany : t.participate.error);
      }
    }
  };

  if (status === "done") {
    return (
      <div data-testid={masterclass ? "masterclass-success" : "visitor-success"} className="flex flex-col items-start gap-3 py-16">
        <CheckCircle2 size={36} className="text-pine" />
        <h3 className="font-serif text-3xl font-medium italic text-ink">
          {masterclass ? c.successTitle : v.successTitle}
        </h3>
        <p className="max-w-md text-ink/65">{masterclass ? c.success : v.success}</p>
      </div>
    );
  }

  if (masterclass && avail && avail.remaining === 0) {
    return (
      <div data-testid="masterclass-soldout" className="flex flex-col items-start gap-3 border border-panettone/30 bg-panettone/5 p-10">
        <GraduationCap size={32} className="text-panettone" />
        <h3 className="font-serif text-3xl font-medium italic text-ink">{c.soldOutTitle}</h3>
        <p className="max-w-md text-ink/65">{c.soldOutText}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={masterclass ? "masterclass-form" : "visitor-form"} className="grid grid-cols-1 gap-7 sm:grid-cols-2">
      {masterclass && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border border-gold/50 bg-gold/10 px-5 py-4 sm:col-span-2" data-testid="masterclass-info">
          <p className="flex items-center gap-3 text-sm font-medium text-ink">
            <GraduationCap size={18} className="shrink-0 text-panettone" />
            {c.info}
          </p>
        </div>
      )}
      <Field label={v.name} htmlFor={masterclass ? "mc-name" : "v-name"}>
        <input id={masterclass ? "mc-name" : "v-name"} data-testid={masterclass ? "masterclass-name-input" : "visitor-name-input"} required maxLength={120} value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} className="field-input" />
      </Field>
      <Field label={v.email} htmlFor={masterclass ? "mc-email" : "v-email"}>
        <input id={masterclass ? "mc-email" : "v-email"} type="email" data-testid={masterclass ? "masterclass-email-input" : "visitor-email-input"} required value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-input" />
      </Field>
      <Field label={v.guests} htmlFor={masterclass ? "mc-guests" : "v-guests"}>
        <input id={masterclass ? "mc-guests" : "v-guests"} type="number" min={1} max={20} data-testid={masterclass ? "masterclass-guests-input" : "visitor-guests-input"} required value={form.guests}
          onChange={(e) => setForm({ ...form, guests: e.target.value })} className="field-input" />
      </Field>
      {!masterclass && (
        <Field label={v.day} htmlFor="v-day">
          <select id="v-day" data-testid="visitor-day-select" value={form.day}
            onChange={(e) => setForm({ ...form, day: e.target.value })} className="field-input bg-cream">
            <option value="2026-12-12">{v.days[0]}</option>
            <option value="2026-12-13">{v.days[1]}</option>
          </select>
        </Field>
      )}
      <div className="sm:col-span-2">
        <button type="submit" data-testid={masterclass ? "masterclass-submit-btn" : "visitor-submit-btn"} disabled={status === "sending"}
          className="group relative overflow-hidden bg-panettone px-10 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-cream disabled:opacity-60">
          <span className="relative z-10 flex items-center gap-2 transition-colors duration-500 group-hover:text-ink">
            {status === "sending" && <Loader2 size={14} className="animate-spin" />}
            {status === "sending" ? v.sending : v.submit}
          </span>
          <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
        </button>
      </div>
    </form>
  );
};

const ExhibitorForm = () => {
  const { lang, t } = useLang();
  const x = t.participate.exhibitor;
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", website: "", description: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/participate/exhibitor`, { ...form, lang });
      setStatus("done");
      toast.success(t.participate.successToast);
    } catch (err) {
      setStatus("idle");
      toast.error(err.response?.status === 429 ? t.participate.tooMany : t.participate.error);
    }
  };

  if (status === "done") {
    return (
      <div data-testid="exhibitor-success" className="flex flex-col items-start gap-3 py-16">
        <CheckCircle2 size={36} className="text-pine" />
        <h3 className="font-serif text-3xl font-medium italic text-ink">{x.successTitle}</h3>
        <p className="max-w-md text-ink/65">{x.success}</p>
      </div>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form onSubmit={submit} data-testid="exhibitor-form" className="grid grid-cols-1 gap-7 sm:grid-cols-2">
      <Field label={x.business} htmlFor="x-business">
        <input id="x-business" data-testid="exhibitor-business-input" required maxLength={120} value={form.business_name} onChange={set("business_name")} className="field-input" />
      </Field>
      <Field label={x.contact} htmlFor="x-contact">
        <input id="x-contact" data-testid="exhibitor-contact-input" required maxLength={120} value={form.contact_name} onChange={set("contact_name")} className="field-input" />
      </Field>
      <Field label={x.email} htmlFor="x-email">
        <input id="x-email" type="email" data-testid="exhibitor-email-input" required value={form.email} onChange={set("email")} className="field-input" />
      </Field>
      <Field label={x.phone} htmlFor="x-phone">
        <input id="x-phone" type="tel" data-testid="exhibitor-phone-input" required maxLength={40} value={form.phone} onChange={set("phone")} className="field-input" />
      </Field>
      <div className="sm:col-span-2">
        <Field label={x.website} htmlFor="x-website">
          <input id="x-website" data-testid="exhibitor-website-input" maxLength={200} value={form.website} onChange={set("website")} className="field-input" placeholder="@yourbakery" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label={x.description} htmlFor="x-description">
          <textarea id="x-description" data-testid="exhibitor-description-input" required minLength={10} maxLength={2000} rows={4}
            value={form.description} onChange={set("description")} className="field-input resize-none" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <button type="submit" data-testid="exhibitor-submit-btn" disabled={status === "sending"}
          className="group relative overflow-hidden bg-ink px-10 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-cream disabled:opacity-60">
          <span className="relative z-10 flex items-center gap-2 transition-colors duration-500 group-hover:text-ink">
            {status === "sending" && <Loader2 size={14} className="animate-spin" />}
            {status === "sending" ? x.sending : x.submit}
          </span>
          <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
        </button>
      </div>
    </form>
  );
};

export const Participate = () => {
  const { t } = useLang();
  const [tab, setTab] = useState("visitor");

  const tabs = [
    { k: "visitor", label: t.participate.tabVisitor },
    { k: "class", label: t.participate.tabClass },
    { k: "exhibitor", label: t.participate.tabExhibitor },
  ];

  return (
    <section id="participate" data-testid="participate-section" className="bg-cream py-24 md:py-36">
      <div className="mx-auto max-w-[1100px] px-6 md:px-14">
        <Reveal>
          <Overline>{t.participate.overline}</Overline>
          <h2 className="mt-5 font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink md:text-6xl">
            {t.participate.title}
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap border-b border-ink/15" data-testid="participate-tabs">
            {tabs.map((tb) => (
              <button
                key={tb.k}
                data-testid={`tab-${tb.k}`}
                onClick={() => setTab(tb.k)}
                className={`relative px-2 py-4 pr-8 text-left font-serif text-xl italic transition-colors duration-300 md:text-2xl ${
                  tab === tb.k ? "text-panettone" : "text-ink/40 hover:text-ink/70"
                }`}
              >
                {tb.label}
                <span className={`absolute bottom-0 left-0 h-0.5 bg-panettone transition-[width] duration-500 ${tab === tb.k ? "w-full" : "w-0"}`} />
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12">
          <h3 className="mb-4 font-serif text-2xl font-medium text-ink/85">
            {tab === "visitor" && t.participate.visitor.title}
            {tab === "class" && t.participate.classForm.title}
            {tab === "exhibitor" && t.participate.exhibitor.title}
          </h3>
          {tab === "class" && <p className="mb-10 max-w-xl text-ink/60">{t.participate.classForm.text}</p>}
          {tab !== "class" && <div className="mb-6" />}
          {tab === "visitor" && <VisitorForm />}
          {tab === "class" && <VisitorForm masterclass />}
          {tab === "exhibitor" && <ExhibitorForm />}
        </div>
      </div>
    </section>
  );
};
